/**
 * Location nesting through `Location.ParentIDLocation`: ancestors, descendants and cycle checks over
 * a customer's location list. Pure functions, so LIMS screens and tests share one walk.
 */

/**
 * @typedef {{ IDLocation: number, ParentIDLocation?: number, Name?: string }} LocationRecord
 */

/**
 * @param {Array<LocationRecord>} pLocations
 * @return {Map<number, LocationRecord>}
 */
function indexByID(pLocations)
{
	const tmpIndex = new Map();
	for (const tmpLocation of (Array.isArray(pLocations) ? pLocations : []))
	{
		if (tmpLocation && Number(tmpLocation.IDLocation))
		{
			tmpIndex.set(Number(tmpLocation.IDLocation), tmpLocation);
		}
	}
	return tmpIndex;
}

/**
 * The chain of parents above a location, outermost first. Stops at a missing parent or a loop.
 *
 * @param {number} pIDLocation
 * @param {Array<LocationRecord>} pLocations
 * @return {Array<LocationRecord>}
 */
function getAncestors(pIDLocation, pLocations)
{
	const tmpIndex = indexByID(pLocations);
	const tmpChain = [];
	const tmpSeen = new Set([ Number(pIDLocation) ]);
	let tmpCurrent = tmpIndex.get(Number(pIDLocation));
	while (tmpCurrent && Number(tmpCurrent.ParentIDLocation))
	{
		const tmpParentID = Number(tmpCurrent.ParentIDLocation);
		if (tmpSeen.has(tmpParentID) || !tmpIndex.has(tmpParentID))
		{
			break;
		}
		tmpSeen.add(tmpParentID);
		tmpCurrent = tmpIndex.get(tmpParentID);
		tmpChain.unshift(tmpCurrent);
	}
	return tmpChain;
}

/**
 * Every location nested anywhere below a location (not including it).
 *
 * @param {number} pIDLocation
 * @param {Array<LocationRecord>} pLocations
 * @return {Array<number>}
 */
function getDescendantIDs(pIDLocation, pLocations)
{
	const tmpChildren = new Map();
	for (const tmpLocation of indexByID(pLocations).values())
	{
		const tmpParentID = Number(tmpLocation.ParentIDLocation);
		if (tmpParentID)
		{
			if (!tmpChildren.has(tmpParentID))
			{
				tmpChildren.set(tmpParentID, []);
			}
			tmpChildren.get(tmpParentID).push(Number(tmpLocation.IDLocation));
		}
	}
	const tmpResult = [];
	const tmpSeen = new Set([ Number(pIDLocation) ]);
	const tmpQueue = [ Number(pIDLocation) ];
	while (tmpQueue.length > 0)
	{
		for (const tmpChildID of (tmpChildren.get(tmpQueue.shift()) || []))
		{
			if (!tmpSeen.has(tmpChildID))
			{
				tmpSeen.add(tmpChildID);
				tmpResult.push(tmpChildID);
				tmpQueue.push(tmpChildID);
			}
		}
	}
	return tmpResult;
}

/**
 * The locations directly inside a location.
 *
 * @param {number} pIDLocation
 * @param {Array<LocationRecord>} pLocations
 * @return {Array<LocationRecord>}
 */
function getChildren(pIDLocation, pLocations)
{
	return Array.from(indexByID(pLocations).values()).filter((pLocation) => Number(pLocation.ParentIDLocation) === Number(pIDLocation));
}

/**
 * True when putting a location inside the proposed parent would make it its own ancestor.
 *
 * @param {number} pIDLocation
 * @param {number} pProposedParentID
 * @param {Array<LocationRecord>} pLocations
 * @return {boolean}
 */
function wouldCreateCycle(pIDLocation, pProposedParentID, pLocations)
{
	const tmpParentID = Number(pProposedParentID);
	if (!tmpParentID || !Number(pIDLocation))
	{
		return false;
	}
	return (tmpParentID === Number(pIDLocation)) || getDescendantIDs(pIDLocation, pLocations).includes(tmpParentID);
}

/**
 * Every location in tree order: each top-level location (A-Z) followed by everything inside it, with its depth.
 * Locations caught in a parent loop come last, at the top level.
 *
 * @param {Array<LocationRecord>} pLocations
 * @return {Array<{ Location: LocationRecord, Depth: number }>}
 */
function getTreeOrder(pLocations)
{
	const tmpIndex = indexByID(pLocations);
	const tmpByName = (pA, pB) => String(pA.Name || '').localeCompare(String(pB.Name || ''), undefined, { numeric: true, sensitivity: 'base' });
	const tmpChildren = new Map();
	const tmpRoots = [];
	for (const tmpLocation of tmpIndex.values())
	{
		const tmpParentID = Number(tmpLocation.ParentIDLocation);
		if (tmpParentID && tmpIndex.has(tmpParentID) && tmpParentID !== Number(tmpLocation.IDLocation))
		{
			if (!tmpChildren.has(tmpParentID))
			{
				tmpChildren.set(tmpParentID, []);
			}
			tmpChildren.get(tmpParentID).push(tmpLocation);
		}
		else
		{
			tmpRoots.push(tmpLocation);
		}
	}
	const tmpOrder = [];
	const tmpVisited = new Set();
	const fVisit = (pLocation, pDepth) =>
	{
		const tmpID = Number(pLocation.IDLocation);
		if (tmpVisited.has(tmpID))
		{
			return;
		}
		tmpVisited.add(tmpID);
		tmpOrder.push({ Location: pLocation, Depth: pDepth });
		for (const tmpChild of (tmpChildren.get(tmpID) || []).sort(tmpByName))
		{
			fVisit(tmpChild, pDepth + 1);
		}
	};
	tmpRoots.sort(tmpByName).forEach((pLocation) => fVisit(pLocation, 0));
	Array.from(tmpIndex.values()).filter((pLocation) => !tmpVisited.has(Number(pLocation.IDLocation))).sort(tmpByName).forEach((pLocation) => fVisit(pLocation, 0));
	return tmpOrder;
}

module.exports =
{
	getAncestors,
	getDescendantIDs,
	getChildren,
	getTreeOrder,
	wouldCreateCycle,
};
